/**
 * @file     TimeSlotPicker.tsx
 * @purpose  Preferred viewing times: suggested day and time-of-day pills
 *           (the OpenHouseTiles pattern) plus "Another time", which opens a
 *           day field and a Morning, Afternoon or Evening control to add one
 *           more. Up to six times; the listing side confirms the exact hour.
 */
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { gateColors } from '@/components/gate/GateSheet';
import { GateSecondaryButton } from '@/components/gate/GateSheet';
import { SheetChip, SheetLabel } from '@/components/lead-capture/ListingSheet';
import { VIEWING_REQUEST_LIMITS, VIEWING_TIME_WINDOWS } from '@/core/config/viewingRequest';
import { useRentalsText } from './useRentalsText';
import { addDays, slotDayLabel, slotKey, toggleSlot, type ViewingSlot, type ViewingWindow } from './viewingRequestModel';

interface Props {
  value: ViewingSlot[];
  onChange: (next: ViewingSlot[]) => void;
  suggestions: ViewingSlot[];
  /** YYYY-MM-DD; the earliest day a renter can add is tomorrow. */
  today: string;
  recipient: 'mls' | 'owner';
  disabled?: boolean;
  idPrefix: string;
}

export function TimeSlotPicker({ value, onChange, suggestions, today, recipient, disabled = false, idPrefix }: Props) {
  const tr = useRentalsText();
  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState('');
  const [window, setWindow] = useState<ViewingWindow | ''>('');
  const labelId = `${idPrefix}-times-label`;
  const chips = [...suggestions, ...value.filter((v) => !suggestions.some((s) => slotKey(s) === slotKey(v)))];
  const selected = new Set(value.map(slotKey));
  const full = value.length >= VIEWING_REQUEST_LIMITS.timesMax;
  const label = (s: ViewingSlot) => tr('viewing.slot', { day: slotDayLabel(s.date), window: tr(`viewing.windowValue.${s.window}`) });
  const minDay = addDays(today, 1);

  const add = () => {
    if (!day || !window || day < minDay) return;
    onChange(toggleSlot(value.filter((v) => slotKey(v) !== `${day}:${window}`), { date: day, window }));
    setAdding(false);
    setDay('');
    setWindow('');
  };

  return (
    <Box role="group" aria-labelledby={labelId} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SheetLabel id={labelId}>{tr('viewing.times')}</SheetLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {chips.map((s) => {
          const pressed = selected.has(slotKey(s));
          return (
            <SheetChip
              key={slotKey(s)}
              pressed={pressed}
              withIcon={pressed}
              onToggle={() => {
                if (disabled || (!pressed && full)) return;
                onChange(toggleSlot(value, s));
              }}
            >
              {label(s)}
            </SheetChip>
          );
        })}
        {!adding ? (
          <SheetChip pressed={false} onToggle={() => !disabled && !full && setAdding(true)}>
            {tr('viewing.anotherTime')}
          </SheetChip>
        ) : null}
      </Box>
      {adding ? (
        <Box data-another-time sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            id={`${idPrefix}-another-day`}
            type="date"
            label={tr('viewing.day')}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: minDay } }}
            fullWidth
            disabled={disabled}
          />
          <SegmentedControl<ViewingWindow | ''>
            options={VIEWING_TIME_WINDOWS.map((w) => ({ value: w, label: tr(`viewing.windowValue.${w}`), disabled }))}
            value={window}
            onChange={setWindow}
            ariaLabel={tr('viewing.window')}
            fullWidth
            id={`${idPrefix}-another-window`}
          />
          <GateSecondaryButton fullWidth onClick={add} disabled={disabled || !day || !window || day < minDay}>
            {tr('viewing.addTime')}
          </GateSecondaryButton>
        </Box>
      ) : null}
      <Typography component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.45, color: gateColors.muted }}>
        {full
          ? tr('viewing.timesMax', { count: VIEWING_REQUEST_LIMITS.timesMax })
          : tr(recipient === 'owner' ? 'viewing.timesHelpOwner' : 'viewing.timesHelp')}
      </Typography>
    </Box>
  );
}
